import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useConfig } from "../lib/configContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { AlertTriangle, Globe2, EyeOff, Loader2 } from "lucide-react";

export function MaintenanceToggle() {
  const { config, refresh } = useConfig();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);

  // Read authoritative value from admin endpoint (also covers cases where
  // /api/config was loaded before login).
  useEffect(() => {
    let alive = true;
    api
      .get("/admin/settings/maintenance")
      .then(({ data }) => {
        if (alive) setEnabled(Boolean(data.enabled));
      })
      .catch((err) => {
        if (alive) setError(formatApiError(err.response?.data?.detail) || "Impossible de lire le mode site.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Keep in sync if ConfigProvider refreshes
  useEffect(() => {
    if (config && typeof config.maintenance === "boolean") {
      setEnabled(config.maintenance);
    }
  }, [config]);

  const requestToggle = (next) => {
    setPendingValue(next);
    setConfirmOpen(true);
  };

  const confirmToggle = async () => {
    if (pendingValue === null) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/admin/settings/maintenance", { enabled: pendingValue });
      setEnabled(Boolean(data.enabled));
      setConfirmOpen(false);
      setPendingValue(null);
      refresh(); // refresh global ConfigProvider so any open public tab updates
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Échec de la mise à jour");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="border-2 border-[#E0D8CE] p-5 mb-10 flex items-center gap-3 text-[#4B4B48]" data-testid="maintenance-block-loading">
        <Loader2 size={16} className="animate-spin" />
        <span className="font-mono-label">Chargement du mode site…</span>
      </div>
    );
  }

  return (
    <>
      <div
        className={`border-2 p-5 mb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${
          enabled ? "border-red-700 bg-red-50" : "border-[#1A1A18] bg-[#FAF8F3]"
        }`}
        data-testid="maintenance-block"
      >
        <div className="flex items-start gap-4">
          {enabled ? (
            <EyeOff size={28} className="text-red-700 flex-shrink-0 mt-1" />
          ) : (
            <Globe2 size={28} className="text-[#4A7A3D] flex-shrink-0 mt-1" />
          )}
          <div>
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <span className="font-mono-label text-[#4B4B48]">Mode site</span>
              {enabled ? (
                <span
                  className="font-mono-label bg-red-700 text-[#FAF8F3] px-2 py-1 inline-flex items-center gap-1.5"
                  data-testid="maintenance-status-badge"
                >
                  <AlertTriangle size={12} /> MAINTENANCE ACTIVE
                </span>
              ) : (
                <span
                  className="font-mono-label bg-[#4A7A3D] text-[#FAF8F3] px-2 py-1"
                  data-testid="maintenance-status-badge"
                >
                  SITE LIVE
                </span>
              )}
            </div>
            <p className="text-sm text-[#4B4B48] leading-relaxed">
              {enabled
                ? "Le public voit la page « Coming Soon ». Les routes /admin restent accessibles. Les emails capturés alimentent la liste de lancement."
                : "Le site est visible publiquement. Active le mode maintenance pour afficher uniquement la page « Bientôt en ligne »."}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => requestToggle(!enabled)}
          disabled={submitting}
          className={`min-h-[44px] px-5 py-3 font-mono-label flex-shrink-0 ${
            enabled
              ? "border-2 border-red-700 text-red-700 hover:bg-red-700 hover:text-[#FAF8F3]"
              : "bg-[#1A1A18] text-[#FAF8F3] hover:bg-[#4A7A3D]"
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          data-testid="maintenance-toggle-btn"
        >
          {enabled ? "Désactiver la maintenance →" : "Activer la maintenance →"}
        </button>
      </div>

      {error && (
        <div className="border-l-4 border-red-700 bg-red-50 p-3 mb-6 text-sm text-red-900" data-testid="maintenance-error">
          {error}
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => !submitting && setConfirmOpen(o)}>
        <DialogContent data-testid="maintenance-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {pendingValue
                ? "Activer le mode maintenance ?"
                : "Désactiver le mode maintenance ?"}
            </DialogTitle>
            <DialogDescription className="text-[#4B4B48] leading-relaxed pt-2">
              {pendingValue
                ? "Le public verra la page « Coming Soon ». Toutes les routes publiques (articles, plaidoyer, ressources, boutique) seront masquées. Les emails capturés via le formulaire alimenteront ta liste de lancement."
                : "Le site sera visible publiquement. Tous les articles, le plaidoyer et la boutique redeviennent accessibles à tous. La page Coming Soon ne s'affichera plus."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-3 sm:gap-2">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
              className="min-h-[44px] border-2 border-[#1A1A18] px-5 py-2 font-mono-label hover:bg-[#F5EFEB] disabled:opacity-50"
              data-testid="maintenance-confirm-cancel"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={confirmToggle}
              disabled={submitting}
              className={`min-h-[44px] px-5 py-2 font-mono-label text-[#FAF8F3] disabled:opacity-50 ${
                pendingValue ? "bg-red-700 hover:bg-red-800" : "bg-[#4A7A3D] hover:bg-[#3E6633]"
              }`}
              data-testid="maintenance-confirm-confirm"
            >
              {submitting ? "Mise à jour…" : pendingValue ? "Oui, activer" : "Oui, désactiver"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
